/**
 * 视频空间展示与访问：封面/海报、签名 batch 选项、安全播放地址、创建人账号展示。
 * 避免将 mp4 等视频 URL 误用作 img src，并与受管存储签名策略一致。
 */
import {

  isManagedStorageUrl,

  isSignedViewEnabled,

  isUploadsStoragePath,

  pickImageSignOptions,

  resolveSignedDisplayForUrl,

} from '@/utils/imageDisplayAccess';

import { resolveVideoSpacePlayUrl } from '@/pages/ipe/workbench/utils/downloadVideoSpaceVideo';

import {

  isLikelyStillImageUrl,

  isVideoFileUrl,

} from '@/pages/ipe/workbench/utils/videoStudioDetailUtils';

import { resolveAuthAccountLabel } from '@/pages/ipe/workbench/utils/resolveAuthDisplayName';



/**

 * 解析视频空间卡片封面（首帧/海报），禁止把 mp4 等成片地址当作 img 源。

 *

 * @param {object|null|undefined} video 视频记录

 * @returns {string} canonical 封面路径，无合适封面时为空串

 */

export function resolveVideoSpacePosterUrl(video) {

  if (!video || typeof video !== 'object') return '';



  const candidates = [

    video.outputVideoPosterUrl,

    video.previewUrl,

    video.posterUrl,

    video.coverUrl,

    video.thumbnailUrl,

    video.thumbUrl,

  ];



  // 1. 按优先级尝试候选字段

  for (const value of candidates) {

    const raw = String(value || '').trim();

    if (!raw) continue;

    // 1.1 跳过明显是视频文件的 URL

    if (isVideoFileUrl(raw)) continue;

    // 1.2 静态图扩展名直接采用

    if (isLikelyStillImageUrl(raw)) return raw;

    // 1.3 路径含 poster/thumb 等关键字且非视频文件

    if (/poster|thumb|cover|frame/i.test(raw.split('?')[0]) && !isVideoFileUrl(raw)) {

      return raw;

    }

  }



  return '';

}



/**

 * 从视频空间条目中组装 sign-batch / useSignedImageSrc 所需的展示字段。

 * 后端 signRecordFields 会为 outputVideoUrl 附加 outputVideoUrlDisplay。

 *

 * @param {object|null|undefined} record 视频空间列表或详情条目

 * @param {string} [playRawOverride] 可选，显式指定 canonical 播放地址

 * @returns {object} 与 pickImageSignOptions 兼容的选项

 */

export function pickVideoSpaceSignOptions(record, playRawOverride = '') {

  const base = pickImageSignOptions(record);

  if (!record || typeof record !== 'object') return base;



  const playRaw = String(

    playRawOverride || resolveVideoSpacePlayUrl(record) || '',

  ).trim();



  const matchedDisplay = playRaw ? resolveSignedDisplayForUrl(playRaw, record) : '';

  const videoDisplay = String(

    matchedDisplay

    || record.outputVideoUrlDisplay

    || record.videoUrlDisplay

    || '',

  ).trim();



  if (!videoDisplay) return base;



  return {

    ...base,

    displayUrl: videoDisplay,

    viewUrl: videoDisplay,

    outputVideoUrlDisplay: record.outputVideoUrlDisplay,

    videoUrlDisplay: record.videoUrlDisplay,

  };

}



/**

 * 解析可用于 video 元素 src 的地址：受管存储禁止回退裸 canonical（会 401）。

 *

 * @param {string} signedSrc 已签名或 async 解析结果

 * @param {string} rawSrc 持久化 canonical

 * @returns {string} 安全可用的播放地址

 */

export function resolveSafeVideoPlaySrc(signedSrc, rawSrc) {

  const signed = String(signedSrc || '').trim();

  if (signed) return signed;



  const raw = String(rawSrc || '').trim();

  if (!raw) return '';



  if (raw.startsWith('blob:') || raw.startsWith('data:')) return raw;



  // 1. 开启签名视图时，uploads/受管路径不允许裸链

  if (isSignedViewEnabled() && (isManagedStorageUrl(raw) || isUploadsStoragePath(raw))) {

    return '';

  }

  if (isManagedStorageUrl(raw)) return '';



  return raw;

}



/**

 * 封面/海报 URL 的 sign 选项（previewUrl、outputVideoPosterUrl 等）。

 *

 * @param {object|null|undefined} record 视频记录

 * @param {string} [posterRaw] canonical 海报地址

 * @returns {object} 签名选项对象

 */

export function pickVideoPosterSignOptions(record, posterRaw = '') {

  const base = pickVideoSpaceSignOptions(record);

  if (!record || typeof record !== 'object') return base;



  const raw = String(posterRaw || '').trim();

  const matched = raw ? resolveSignedDisplayForUrl(raw, record) : '';

  const posterDisplay = String(

    matched

    || record.outputVideoPosterUrlDisplay

    || record.previewDisplayUrl

    || record.previewViewUrl

    || '',

  ).trim();



  if (!posterDisplay) return base;



  return {

    ...base,

    displayUrl: posterDisplay,

    viewUrl: posterDisplay,

    previewDisplayUrl: record.previewDisplayUrl,

    previewViewUrl: record.previewViewUrl,

  };

}



/**

 * 从记录字段解析 owner 用户 id（兼容多种后端字段名）。

 *

 * @param {object|null|undefined} record 视频记录

 * @returns {string}

 */

function resolveVideoRecordOwnerUserId(record) {

  const raw = record?.ownerUserId ?? record?.ownerId ?? record?.owner_id

    ?? record?.createdByUserId ?? record?.userId ?? record?.user_id ?? '';

  return raw != null && raw !== '' ? String(raw) : '';

}



/**

 * 从记录上直接读取创建人账号展示字段（手机号/用户名等）。

 *

 * @param {object|null|undefined} record 视频记录

 * @returns {string}

 */

function pickVideoRecordAccountFromFields(record) {

  if (!record || typeof record !== 'object') return '';

  const keys = ['ownerPhone', 'ownerUsername', 'ownerAccount', 'username', 'phone'];

  for (const key of keys) {

    const value = String(record[key] || '').trim();

    if (!value || value === '—') continue;

    return value;

  }

  return '';

}



/**

 * 从团队成员对象解析账号展示文案。

 *

 * @param {object|null|undefined} member 团队成员

 * @returns {string}

 */

function resolveTeamMemberAccountLabel(member) {

  if (!member || typeof member !== 'object') return '';

  const phone = String(member.phone || '').trim();

  if (phone) return phone;

  const username = String(member.username || '').trim();

  if (username) return username;

  return '';

}



/**

 * 视频预览「创建人」：展示登录账号（手机号等），不使用昵称字段。

 *

 * @param {object|null|undefined} record 视频记录

 * @param {{ authUserId?: string|number, user?: object|null, teamMembers?: object[] }} [options]

 * @returns {string} 账号文案，无法解析时为「—」

 */

export function resolveVideoSpaceCreatorAccountLabel(record, options = {}) {

  const { authUserId = '', user = null, teamMembers = [] } = options;



  // 1. 记录自带账号字段

  const fromRecord = pickVideoRecordAccountFromFields(record);

  if (fromRecord) return fromRecord;



  const ownerId = resolveVideoRecordOwnerUserId(record);

  const viewerId = authUserId != null && authUserId !== '' ? String(authUserId) : '';



  // 2. 本人创建：用当前登录用户账号

  if (viewerId && ownerId && ownerId === viewerId) {

    const selfAccount = resolveAuthAccountLabel(user);

    if (selfAccount) return selfAccount;

  }



  // 3. 团队模式：在成员列表中匹配 owner

  if (ownerId && Array.isArray(teamMembers) && teamMembers.length > 0) {

    const member = teamMembers.find((item) => String(item.userId ?? item.id) === ownerId);

    const account = resolveTeamMemberAccountLabel(member);

    if (account) return account;

  }



  return '—';

}

